import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DASHBOARD } from "@/constants/testIds";
import {
  Truck, ClipboardCheck, Footprints, ShieldOff, ShieldCheck,
  DoorOpen, PackageCheck, PackagePlus, TriangleAlert as AlertTriangle,
  Clock, LayoutPanelTop, FileText, RefreshCw, Wrench, Users, Link as LinkIcon,
  Layers, BarChart3
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Dashboard() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: stats, isLoading } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: () => api.get("/dashboard/stats").then((r) => r.data),
  });

  const isManager = user?.role === "admin" || user?.role === "supervisor" || user?.email?.includes("d.trujillo");

  const [downloading, setDownloading] = useState(false);

  const handleDownloadReport = async () => {
    try {
      setDownloading(true);

      if (window.Capacitor?.isNativePlatform()) {
        const token = localStorage.getItem("sisp_token");
        const url = `${api.defaults.baseURL}reports/consolidated/pdf?token=${token}`;
        window.open(url, '_blank');
        return;
      }

      const res = await api.get("/reports/consolidated/pdf", { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'reporte_consolidado.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error("Error al descargar reporte");
    } finally {
      setDownloading(false);
    }
  };

  const syncMutation = useMutation({
    mutationFn: () => api.post("/sync/sriuc/run"),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Sincronización completada");
    },
    onError: () => toast.error("Error al sincronizar"),
  });

  const repairMutation = useMutation({
    mutationFn: () => api.post("/sync/sriuc/repair").then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success(`Reparación completada: ${data.processed || 0} registros procesados`);
    },
    onError: (e) => {
      console.error("Error en reparación:", e);
      toast.error(apiError(e, "Error al reparar bitácoras"));
    },
  });

  const cards = [
    { label: "Registros SIRUC", value: stats?.records, icon: Truck, testid: DASHBOARD.statRecords, to: "/siruc/records" },
    { label: "Inspecciones", value: stats?.inspections, icon: ClipboardCheck, testid: DASHBOARD.statInspections, to: "/siruc/inspections" },
    { label: "Rondas", value: stats?.recorridos, icon: Footprints, testid: DASHBOARD.statRecorridos, to: "/recorridos" },
    { label: "Sellos Rotos", value: stats?.sellos_retirados, icon: ShieldOff, testid: DASHBOARD.statSellosRotos, to: "/bitacora/sellos_retirados", accent: "critical" },
    { label: "Sellos Colocados", value: stats?.sellos_colocados, icon: ShieldCheck, testid: DASHBOARD.statSellosColocados, to: "/bitacora/sellos_colocados", accent: "success" },
    { label: "Embarques", value: stats?.embarques, icon: PackageCheck, testid: DASHBOARD.statEmbarques, to: "/bitacora/embarque" },
    { label: "Recibos", value: stats?.recibos, icon: PackagePlus, testid: DASHBOARD.statRecibos, to: "/bitacora/recibo" },
    { label: "Tickets Espejo", value: stats?.sriuc_tickets, icon: FileText, testid: "stat-sriuc-t", to: "/siruc/tickets", accent: "success" },
  ];

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto space-y-6 lg:space-y-8">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-black tracking-tighter text-navy uppercase italic">NAF <span className="text-muted-foreground font-light not-italic text-2xl ml-2">Control</span></h1>
          <p className="text-[10px] lg:text-sm text-muted-foreground font-medium uppercase tracking-widest mt-1 opacity-60">Resumen operativo del sistema</p>
        </div>
        <div className="flex gap-2">
          {isManager && (
            <button
              onClick={handleDownloadReport}
              disabled={downloading}
              className="flex items-center justify-center gap-2 px-3 lg:px-4 h-10 border border-navy text-navy text-[10px] font-black uppercase tracking-widest hover:bg-navy/5 transition-all disabled:opacity-50 flex-1 md:flex-none"
            >
              <FileText size={14} />
              {downloading ? "..." : "Reporte PDF"}
            </button>
          )}
          <button
            onClick={() => syncMutation.mutate()}
            className="flex items-center justify-center gap-2 px-3 lg:px-4 h-10 bg-success text-white text-[10px] font-black uppercase tracking-widest hover:bg-success/90 transition-all shadow-lg shadow-success/20 flex-1 md:flex-none"
          >
            <RefreshCw size={14} className={syncMutation.isPending ? "animate-spin" : ""} />
            Sincronizar
          </button>
        </div>
      </header>

      {/* Herramientas de Administración (Solo para Admin/Supervisor) */}
      {isManager && (
        <section className="bg-navy/5 border-2 border-navy/10 p-4">
          <p className="text-[10px] font-black tracking-[0.2em] text-navy/40 mb-4 uppercase">Herramientas Globales</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <AdminTool icon={Wrench} label="Reparar Bitácoras" onClick={() => repairMutation.mutate()} variant="navy" />
            <AdminTool icon={LinkIcon} label="Vincular Huérfanos" />
            <AdminTool icon={Users} label="Usuarios" onClick={() => navigate("/users")} />
            <AdminTool icon={BarChart3} label="KPIs / Reportes" />
          </div>
        </section>
      )}

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4">
        {cards.map((c) => (
          <Link
            key={c.testid}
            to={c.to}
            className="bg-white border-2 border-border p-4 lg:p-5 flex flex-col gap-0.5 lg:gap-1 hover:border-navy transition-all group relative overflow-hidden"
          >
            <div className="flex items-center justify-between relative z-10">
              <span className={`w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center ${
                c.accent === "critical" ? "bg-critical/10 text-critical" :
                c.accent === "success" ? "bg-success/10 text-success" : "bg-navy/5 text-navy"
              }`}>
                <c.icon size={18} />
              </span>
            </div>
            <p className="text-3xl lg:text-4xl font-black tracking-tighter text-navy mt-1 lg:mt-2 group-hover:scale-110 transition-transform origin-left italic">
              {isLoading ? "—" : c.value ?? 0}
            </p>
            <p className="text-[9px] lg:text-[10px] font-bold text-muted-foreground uppercase tracking-widest truncate">{c.label}</p>
          </Link>
        ))}
      </section>

      <section className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-border p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg lg:text-xl font-bold text-navy">Novedades recientes</h2>
            <Link to="/recorridos" className="text-xs lg:text-sm text-navy hover:underline">Ver todas</Link>
          </div>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : stats?.novedades_recientes?.length ? (
            <ul data-testid={DASHBOARD.novedadesList} className="divide-y divide-border">
              {stats.novedades_recientes.map((n) => (
                <li key={n.id} className="py-3 flex items-start gap-3">
                  <span className={`mt-1.5 w-1.5 h-1.5 shrink-0 ${
                    n.severidad === "alta" ? "bg-critical" : n.severidad === "media" ? "bg-warn" : "bg-success"
                  }`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-navy truncate uppercase tracking-tight">{n.titulo}</p>
                    <p className="text-[10px] text-muted-foreground truncate uppercase font-medium">{n.ubicacion || "Sin ubicación"}</p>
                    {n.novedad && <p className="text-xs text-muted-foreground mt-1 line-clamp-1 opacity-80">{n.novedad}</p>}
                  </div>
                  <Link to={`/recorridos/${n.id}`} className="text-[10px] font-black text-navy hover:underline shrink-0 uppercase">Detalle</Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Sin novedades registradas.</p>
          )}
        </div>

        <div className="bg-white border border-border p-5">
          <h2 className="font-heading text-lg lg:text-xl font-bold text-navy mb-4">Accesos rápidos</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isManager && <QuickLink to="/master" label="Panel Maestro" icon={LayoutPanelTop} />}
            {isManager && (
              <button
                onClick={() => repairMutation.mutate()}
                className="flex items-center gap-3 p-3 lg:p-4 border border-navy bg-navy/5 hover:bg-navy hover:text-white transition-colors group text-left"
              >
                <span className="w-8 h-8 lg:w-9 lg:h-9 bg-navy text-white flex items-center justify-center shrink-0">
                  <Wrench size={18} className={repairMutation.isPending ? "animate-spin" : ""} />
                </span>
                <span className="text-xs lg:text-sm font-bold uppercase tracking-tight">Reparar Bitácoras</span>
              </button>
            )}
            <QuickLink to="/siruc/records" label="Nuevo SIRUC" icon={Truck} />
            <QuickLink to="/siruc/inspections" label="Inspección C-TPAT" icon={ClipboardCheck} />
          </div>
        </div>
      </section>
    </div>
  );
}

function QuickLink({ to, label, icon: Icon }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 p-4 border border-border hover:border-navy hover:bg-secondary transition-colors group"
    >
      <span className="w-9 h-9 bg-navy/5 text-navy flex items-center justify-center group-hover:bg-navy group-hover:text-white transition-colors">
        <Icon size={18} />
      </span>
      <span className="text-sm font-medium text-navy">{label}</span>
    </Link>
  );
}

function AdminTool({ icon: Icon, label, onClick, variant = "default" }) {
  const colors = {
    default: "bg-white border-navy/20 text-navy hover:bg-navy hover:text-white",
    warn: "bg-warn/10 border-warn/30 text-warn-foreground hover:bg-warn hover:text-white",
    navy: "bg-navy text-white hover:bg-navy-hover"
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-center gap-2 p-3 border-2 transition-all font-bold text-[11px] uppercase tracking-wider ${colors[variant]}`}
    >
      <Icon size={16} />
      {label}
    </button>
  );
}
